import { useState } from 'react'
import { Box, Typography, Chip, Collapse, IconButton, Tooltip } from '@mui/material'
import { ExpandMore, ExpandLess } from '@mui/icons-material'

interface PermissionFitBreakdownProps {
  // The single MVPS-001 finding row for this agent, as returned by
  // GET /analysis/{id}/findings (expected_tier/actual_tier/excess_tools/
  // inferred_via/inference_evidence are only present on MVPS-001 rows —
  // see services/analysis/main.py's get_analysis_findings allowlist).
  finding: any
}

const TIER_LABEL: Record<string, string> = {
  read: 'read', write: 'write', execute: 'execute', delete: 'delete',
}

/**
 * Findings-pipeline UI fix (2026-07-29) — surfaces MVPS-001's structured
 * reasoning (expected vs. actual tool tier, the specific excess tools,
 * and the evidence behind the expected-tier inference) instead of
 * leaving it as a text row in the Findings tab. Mirrors
 * CostFactorsBreakdown.tsx's always-visible-summary + expandable-detail
 * pattern exactly (manual expand state + Collapse, not MUI Accordion).
 */
const PermissionFitBreakdown = ({ finding }: PermissionFitBreakdownProps) => {
  const [expanded, setExpanded] = useState(false)

  if (!finding || !finding.expected_tier || !finding.actual_tier) return null

  const expected = finding.expected_tier as string
  const actual = finding.actual_tier as string
  const excessTools: string[] = finding.excess_tools || []
  const via = finding.inferred_via as string | undefined
  const evidence = finding.inference_evidence

  const evidenceTooltip = via === 'keyword'
    ? `Expected tier inferred from role text: "${evidence}"`
    : via === 'semantic'
    ? `Expected tier inferred by semantic similarity to the ${TIER_LABEL[expected] || expected}-tier role description (similarity ${evidence})`
    : 'Expected tier could not be traced to specific evidence'

  return (
    <Box sx={{ mt: 0.5 }}>
      {/* Always-visible summary */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        <Tooltip title="Inferred from this agent's own stated role text — the tier its role justifies.">
          <Chip
            label={`Expected: ${TIER_LABEL[expected] || expected}-tier`}
            size="small" variant="outlined"
            sx={{ fontSize: '0.6rem', height: 20 }}
          />
        </Tooltip>
        <Tooltip title="The highest tool-permission tier this agent actually holds, right now.">
          <Chip
            label={`Actual: ${TIER_LABEL[actual] || actual}-tier`}
            size="small" variant="outlined"
            color={finding.severity === 'critical' ? 'error' : 'warning'}
            sx={{ fontSize: '0.6rem', height: 20 }}
          />
        </Tooltip>
      </Box>

      {/* Expandable details */}
      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mt: 0.5 }}
        onClick={() => setExpanded(!expanded)}>
        <IconButton size="small">
          {expanded ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
        </IconButton>
        <Typography variant="caption" color="text.secondary">
          {expanded ? 'Hide' : 'Show'} excess tools
        </Typography>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ pl: 2, pt: 0.5, pb: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {excessTools.length > 0 ? excessTools.map((tool) => (
            <Tooltip key={tool} title={evidenceTooltip}>
              <Chip
                label={tool} size="small" variant="outlined"
                color={via === 'keyword' ? 'success' : 'default'}
                sx={{ fontSize: '0.6rem', height: 20 }}
              />
            </Tooltip>
          )) : (
            <Typography variant="caption" color="text.secondary">
              No specific excess tools recorded
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}

export default PermissionFitBreakdown
